/**
 * Utility functions for sanitizing user input to prevent XSS attacks
 */

/**
 * Sanitizes HTML content
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string
 */
export const sanitizeHtml = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }
  return html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Sanitizes text content by encoding HTML entities
 * @param text - The text string to sanitize
 * @returns Sanitized text string
 */
export const sanitizeText = (text: string): string => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Validates if a string is a safe URL
 * @param url - The URL to validate
 * @returns True if the URL is safe, false otherwise
 */
export const isValidUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const urlObj = new URL(url);
    // Allow http, https, data, and blob URLs
    return ['http:', 'https:', 'data:', 'blob:'].includes(urlObj.protocol);
  } catch {
    return false;
  }
};

/**
 * Sanitizes a URL for safe use in src attributes
 * @param url - The URL to sanitize
 * @returns Sanitized URL or empty string if unsafe
 */
export const sanitizeUrl = (url: string): string => {
  if (!isValidUrl(url)) {
    return '';
  }

  return url;
};

/**
 * Sanitizes an image URL for safe use in img src attributes
 * @param url - The image URL to sanitize
 * @returns Sanitized image URL or empty string if unsafe
 */
export const sanitizeImageUrl = (url: string): string => {
  if (!url || typeof url !== 'string') {
    return '';
  }

  // Check if it's a valid URL
  if (!isValidUrl(url)) {
    return '';
  }

  // For data URLs, ensure they're image data URLs
  if (url.startsWith('data:') && !url.startsWith('data:image/')) {
    return '';
  }

  return url;
};

/**
 * Sanitizes file names for safe display
 * @param fileName - The file name to sanitize
 * @returns Sanitized file name
 */
export const sanitizeFileName = (fileName: string): string => {
  if (!fileName || typeof fileName !== 'string') {
    return '';
  }

  // First, remove any content within angle brackets (potential HTML tags or script injection)
  let cleanName = fileName.replace(/<[^>]*>/g, '');
  // Then, remove any other invalid filename characters
  cleanName = cleanName.replace(/[<>:"/\\|?*]/g, '').trim();
  return sanitizeText(cleanName);
};
